/*
 * myPokemonDexLink.ts — the DEFERRED half of "owning a Pokémon proves the
 * dex tick" (2026-09-02 ask).
 *
 * myPokemonDexCrossWrite.ts is the bulk, fire-and-forget pass: every record,
 * every flag, run on import and on the list page's mount. This module is the
 * per-visit one. The My Pokémon detail page's status toggles used to write the
 * Pokédex the instant they were tapped, silently: tick Shiny, and a cell three
 * screens away changed with nothing said about it and no way back.
 *
 * So the page now BATCHES a visit's toggles and commits them on the way out
 * (back, a menu, or a swipe to the next Pokémon), publishing what it wrote here
 * so a single bar can say "Relevant Pokédex entries have been updated" with an
 * Undo beside it.
 *
 * The undo reverts the WHOLE change, not just the dex cells: the cells this
 * commit ticked AND the record flags that vouched for them. Cells alone would
 * not hold — the bulk pass still reads those flags, so the next visit to the
 * list would tick the same cells straight back and the undo would quietly come
 * apart. Undoing the statement is what makes the retraction stick.
 *
 * Same three rules as the bulk pass, for the same reasons: additive only
 * (a toggle never clears a cell it did not just write), roster-gated, and
 * governed by the Settings "Update Pokédex" switch.
 */
import { clearManual, getDexEntryState, setManual } from "./dexStateStore";
import { isDexAutoUpdateEnabled } from "./dexAutoUpdatePref";
import { isVariantAvailable } from "./variantAvailability";
import { formDexCell, formDexRegistryForm, isFormDexVariant } from "./formDexRoster";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import {
  isPerfect,
  setRecordFormId,
  setRecordSizeClass,
  setRecordVariantFlags,
  type MyPokemonRecord,
  type MyPokemonSizeClass,
} from "./myPokemonStore";

/** One Pokédex cell a record vouches for. */
export interface DexLinkCell {
  variant: PokedexVariant;
  dexNumber: number;
  /** Form-dex cells (Regional / Costume) are keyed by form; species cells are not. */
  formId: string | null;
}

/**
 * The record flag behind each togglable dex variant. Best Buddy is absent on
 * purpose: it has no dex, it feeds the level-51 PvP cap instead.
 */
export const DEX_FLAG_BY_VARIANT = {
  shiny: "isShiny",
  lucky: "isLucky",
  shadow: "isShadow",
  purified: "isPurified",
  mega: "isMega",
  dynamax: "isDynamax",
  gmax: "isGmax",
} as const satisfies Partial<Record<PokedexVariant, keyof MyPokemonRecord>>;

export type DexFlagVariant = keyof typeof DEX_FLAG_BY_VARIANT;

/** The same table read the other way: record flag -> the dex it vouches for. */
export const DEX_VARIANT_BY_FLAG = Object.fromEntries(
  Object.entries(DEX_FLAG_BY_VARIANT).map(([variant, flag]) => [flag, variant]),
) as Partial<Record<string, DexFlagVariant>>;

/**
 * The cells `variants` mean for THIS record, dropped where they cannot exist.
 *
 * A form dex (Costume) has no species-level cell: its truth is the record's own
 * formId, and an unrecognised id writes nothing rather than a junk row. Species
 * variants take the same roster gate the grid uses, so a data-error row can
 * never push a curated dex above its released roster.
 */
export function dexCellsForRecord(
  record: MyPokemonRecord,
  variants: readonly PokedexVariant[],
): DexLinkCell[] {
  const dexNumber = record.speciesId;
  if (dexNumber == null) return [];
  const cells: DexLinkCell[] = [];
  for (const variant of variants) {
    if (isFormDexVariant(variant)) {
      const formId = record.formId;
      if (!formId || !formDexRegistryForm(dexNumber, formId)) continue;
      cells.push({ variant, dexNumber, formId });
      continue;
    }
    if (!isVariantAvailable(variant, dexNumber)) continue;
    cells.push({ variant, dexNumber, formId: null });
  }
  return cells;
}

/**
 * Which form dex a species' form belongs to, or null for a form that is on
 * neither (a mechanical form like Giratina Origin, or an unknown id). Costume
 * is asked first; the two rosters are disjoint, so the order is a tie-break
 * that cannot actually fire.
 */
export function formDexVariantFor(
  dexNumber: number,
  formId: string | null | undefined,
): PokedexVariant | null {
  if (!formId) return null;
  if (formDexCell("costume", dexNumber, formId)) return "costume";
  if (formDexCell("regional", dexNumber, formId)) return "regional";
  return null;
}

/**
 * Tick the cells, and report only the ones that were NOT already present —
 * those are what an undo may take back. Cells already ticked are somebody
 * else's catch (a scan, a manual tap, an earlier visit) and this pass neither
 * rewrites them nor claims them.
 */
export function applyDexLink(cells: readonly DexLinkCell[]): DexLinkCell[] {
  if (!isDexAutoUpdateEnabled()) return [];
  const written: DexLinkCell[] = [];
  for (const cell of cells) {
    const prev = getDexEntryState(cell.variant, cell.dexNumber, cell.formId);
    if (prev?.state === "present") continue;
    if (setManual(cell.variant, cell.dexNumber, "present", cell.formId)) written.push(cell);
  }
  return written;
}

// ── The one-slot commit buffer the undo bar reads ────────────────────────────
// Same shape as myPokemonStore's deletion buffer: latest commit only, and the
// bar decides for itself how long to keep offering the undo. Lives here rather
// than in page state because the commit happens as the page LEAVES — the bar
// that shows it is mounted globally and has to outlive the detail screen.

/** The record fields a visit can change, as they were before it. */
export type DexLinkFlagsBefore = Partial<
  Pick<
    MyPokemonRecord,
    "isShiny" | "isLucky" | "isShadow" | "isPurified" | "isMega" | "isDynamax" | "isGmax"
  >
>;

export interface DexLinkCommit {
  /** The record whose toggles caused this, for the flag revert. */
  recordId: string;
  /** Cells this commit newly ticked. */
  cells: DexLinkCell[];
  /** The flags as they stood before the visit's toggles. */
  before: DexLinkFlagsBefore;
  /** The form as it stood before, or undefined when the visit left it alone. */
  beforeFormId?: string | null;
  /** The size as it stood before, or undefined when the visit left it alone. */
  beforeSizeClass?: MyPokemonSizeClass;
  at: number;
}

/** The dex a size read vouches for, or null for XS / XL / none (no dex). */
export function sizeDexVariant(sizeClass: MyPokemonSizeClass): PokedexVariant | null {
  if (sizeClass === "xxl") return "xxl";
  if (sizeClass === "xxs") return "xxs";
  return null;
}

/**
 * The dexes a visit's changes vouch for, INCLUDING the ones that follow from
 * them (2026-09-10 owner ask: a record set up right lands in every variant it
 * qualifies for). Shiny + Lucky is the Shiny Lucky dex; Shiny on a hundo is
 * the Shundo dex; and a hundo vouches for 100% whenever its shininess or
 * luckiness was the thing that changed. Read off the record as it stands now,
 * so a flag switched back off inside the visit implies nothing.
 */
export function impliedDexVariants(
  record: MyPokemonRecord,
  touched: ReadonlySet<PokedexVariant>,
): PokedexVariant[] {
  const out = new Set<PokedexVariant>(touched);
  const flagged = touched.has("shiny") || touched.has("lucky");
  if (flagged && record.isShiny && record.isLucky) out.add("shinyLucky");
  if (flagged && isPerfect(record)) {
    out.add("perfect");
    if (record.isShiny) out.add("shundo");
  }
  return [...out];
}

let lastCommit: DexLinkCommit | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of [...listeners]) fn();
}

export function subscribeDexLinkCommit(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The latest commit still offering an undo, or null. */
export function peekDexLinkCommit(): DexLinkCommit | null {
  return lastCommit;
}

/** Publish a commit for the undo bar. A no-op commit publishes nothing. */
export function publishDexLinkCommit(commit: DexLinkCommit): void {
  if (commit.cells.length === 0) return;
  lastCommit = commit;
  emit();
}

/** Drop the offer (the bar timed out, or the undo was taken). */
export function clearDexLinkCommit(): void {
  if (lastCommit == null) return;
  lastCommit = null;
  emit();
}

/**
 * Take back the latest commit: untick the cells it wrote, then restore the
 * record flags that vouched for them (see the module header — cells alone
 * would be re-ticked by the bulk pass). Returns the number of cells cleared.
 */
export function undoDexLinkCommit(): number {
  const commit = lastCommit;
  if (!commit) return 0;
  lastCommit = null;
  let cleared = 0;
  for (const cell of commit.cells) {
    if (clearManual(cell.variant, cell.dexNumber, cell.formId)) cleared += 1;
  }
  if (Object.keys(commit.before).length > 0) {
    setRecordVariantFlags(commit.recordId, commit.before);
  }
  if (commit.beforeFormId !== undefined) {
    setRecordFormId(commit.recordId, commit.beforeFormId);
  }
  if (commit.beforeSizeClass !== undefined) {
    setRecordSizeClass(commit.recordId, commit.beforeSizeClass);
  }
  emit();
  return cleared;
}
